import { fabric } from 'fabric';
import { renderFixSpellingControl, handleFixSpellingClick } from './controls';
import {
  getControlSizes,
  getControlPositionOffsets,
  controlConfig,
} from './controls/controlConfig';
import { SPELLCHECK_COLORS } from './constants';

/**
 * SpellCheckTextbox extends fabric.Textbox to add spellcheck visualization
 * Displays wavy underlines beneath misspelled words:
 * - Red for errors with fixes (fixable)
 * - Orange for warnings/informational (no fixes available)
 */
export class SpellCheckTextbox extends fabric.Textbox {
  /**
   * Constructor
   * @param {string} text - The text content
   * @param {object} options - Fabric.js options
   */
  constructor(text, options) {
    super(text, options);

    // Initialize spellcheck-specific properties
    this.spellCheckResults = null;
    this.spellCheckEnabled = true;
    this._cachedTextMetrics = null;
    // True once underlines are drawn, until the cache canvas is dropped
    this._spellCheckUnderlinesDrawn = false;

    // Spellcheck underline styling constants - now scale with font size
    this.UNDERLINE_COLOR_FIXABLE = SPELLCHECK_COLORS.FIXABLE; // Red for errors with fixes
    this.UNDERLINE_COLOR_INFORMATIONAL = SPELLCHECK_COLORS.INFORMATIONAL; // Orange for warnings/informational
    this.UNDERLINE_WIDTH_RATIO = 0.02; // Relative to font size - back to normal thickness
    this.WAVY_AMPLITUDE_RATIO = 0.04; // Relative to font size - reduced for shorter waves
    this.WAVY_WAVELENGTH_RATIO = 0.3; // Relative to font size
    this.UNDERLINE_OFFSET_RATIO = 0.15; // Distance below baseline, relative to font size

    // Ensure backward compatibility by setting the type
    this.type = 'textbox';

    // Initialize controls from parent class
    this.controls = { ...fabric.Textbox.prototype.controls };

    // Add spell check control
    this._initializeSpellCheckControl();
  }

  /**
   * Initialize the spell check control for this instance
   * @private
   */
  _initializeSpellCheckControl() {
    // Create control with dynamic size calculation
    this.controls.fixSpelling = new fabric.Control({
      x: controlConfig.position.x,
      y: controlConfig.position.y,
      // Use getters for dynamic offset calculation
      get offsetY() {
        const { offsetY } = getControlPositionOffsets();
        return offsetY;
      },
      get offsetX() {
        const { offsetX } = getControlPositionOffsets();
        return offsetX;
      },
      // Use getters for dynamic size calculation
      get sizeX() {
        const { sizeX } = getControlSizes();
        return sizeX;
      },
      get sizeY() {
        const { sizeY } = getControlSizes();
        return sizeY;
      },
      get touchSizeX() {
        const { sizeX } = getControlSizes();
        return sizeX;
      },
      get touchSizeY() {
        const { sizeY } = getControlSizes();
        return sizeY;
      },
      padding: controlConfig.padding,
      cursorStyle: 'pointer',
      visible: true,
      mouseUpHandler: handleFixSpellingClick,
      render: renderFixSpellingControl,
      // Override getVisibility to always show during editing if there are errors
      getVisibility: (fabricObject) => {
        // Always visible if there are spell check errors
        if (
          fabricObject &&
          fabricObject.hasSpellCheckErrors &&
          fabricObject.hasSpellCheckErrors()
        ) {
          return true;
        }
        // Otherwise use default visibility logic
        return fabricObject && fabricObject.visible && !fabricObject.isEditing;
      },
      // Make control actionable even during editing
      getActionHandler: () => {
        return handleFixSpellingClick;
      },
      // Ensure control is interactive during editing
      getMouseDownHandler: () => {
        return handleFixSpellingClick;
      },
    });
  }

  /**
   * Update control sizes when zoom changes
   * This should be called whenever the canvas zoom is updated
   */
  updateControlSizes() {
    if (this.controls.fixSpelling) {
      // Force re-render to pick up new sizes
      this.dirty = true;
      this.canvas && this.canvas.requestRenderAll();
    }
  }

  /**
   * Set spellcheck results
   * @param {object} results - Spellcheck results from LanguageTool API
   */
  setSpellCheckResults(results) {
    this.spellCheckResults = results;
    this._cachedTextMetrics = null;

    // Force text measurement to ensure _textLines is populated
    if (!this._textLines || this._textLines.length === 0) {
      this.initDimensions();
    }

    this.dirty = true;
    this.canvas && this.canvas.requestRenderAll();
  }

  /**
   * Clear spellcheck results
   */
  clearSpellCheckResults() {
    this.spellCheckResults = null;
    this._cachedTextMetrics = null;
    this.dirty = true;
    this.canvas && this.canvas.requestRenderAll();
  }

  /**
   * Enable or disable spellcheck visualization
   * @param {boolean} enabled
   */
  setSpellCheckEnabled(enabled) {
    this.spellCheckEnabled = enabled;
    this.dirty = true;
    this.canvas && this.canvas.requestRenderAll();
  }

  /**
   * Whether spellcheck underlines can show up in an export: they are drawn
   * now, or were drawn before and may still sit in the cache canvas outside
   * the region fabric clears on a dirty-only redraw.
   * @returns {boolean}
   */
  hasSpellCheckUnderlinesToHide() {
    return (
      (this.spellCheckEnabled && this.hasSpellCheckErrors()) ||
      this._spellCheckUnderlinesDrawn
    );
  }

  /**
   * Temporarily hide spellcheck for export, returns a function to restore
   * @returns {Function} Function to restore spellcheck visibility
   */
  hideSpellCheckForExport() {
    const wasEnabled = this.spellCheckEnabled;
    this.spellCheckEnabled = false;
    // Underlines are measured without charSpacing/kerning and positioned with
    // per-line height, so they can extend past the object bounds. Fabric's
    // dirty-only cache redraw clears only the object-sized region, leaving
    // that overflow in the larger cache canvas where it leaks into
    // exports/prints. Drop the cache so it is fully recreated without the
    // underlines instead of clipping them (which would hide valid on-screen
    // feedback for large lineHeight or selection font sizes).
    this._invalidateSpellCheckCache();
    // Don't trigger render during export - the export process will handle rendering

    // Return function to restore original state
    return () => {
      this.spellCheckEnabled = wasEnabled;
      this._invalidateSpellCheckCache();
      // Don't trigger render during export - the export process will handle rendering
    };
  }

  /**
   * Force a clean rebuild of this object's cache canvas on the next render,
   * discarding any stale spellcheck pixels that fall outside the region
   * fabric clears on a dirty-only redraw.
   * @private
   */
  _invalidateSpellCheckCache() {
    this.dirty = true;
    if (typeof this._removeCacheCanvas === 'function') {
      this._removeCacheCanvas();
    }
    this._spellCheckUnderlinesDrawn = false;
  }

  /**
   * Override _clearCache to also clear our text metrics cache
   */
  _clearCache() {
    super._clearCache && super._clearCache();
    this._cachedTextMetrics = null;
  }

  /**
   * Override initDimensions to clear cache when dimensions change
   */
  initDimensions() {
    super.initDimensions();
    this._cachedTextMetrics = null;
  }

  /**
   * Get character boundaries for a given position
   * @param {number} charIndex
   * @param {number} lineIndex
   * @returns {object|null} Character boundaries
   */
  _getCharBoundaries(charIndex, lineIndex = 0) {
    if (!this.__charBounds || !this.__charBounds[lineIndex]) {
      return null;
    }

    const boundaries = this.__charBounds[lineIndex][charIndex];
    return boundaries || null;
  }

  /**
   * Get font size scaled properties for consistent rendering
   * @param {number} [specificFontSize] - Optional specific font size to use
   * @returns {object} Scaled properties
   */
  _getScaledProperties(specificFontSize) {
    const fontSize = specificFontSize || this.fontSize || 20;
    return {
      underlineWidth: Math.max(1, fontSize * this.UNDERLINE_WIDTH_RATIO),
      wavyAmplitude: fontSize * this.WAVY_AMPLITUDE_RATIO,
      wavyWavelength: fontSize * this.WAVY_WAVELENGTH_RATIO,
      underlineOffset: fontSize * this.UNDERLINE_OFFSET_RATIO,
    };
  }

  /**
   * Get the cumulative height of all lines up to (but not including) the specified line
   * @param {number} lineIndex - The line index
   * @returns {number} Cumulative height
   */
  _getCumulativeLineHeight(lineIndex) {
    let height = 0;
    for (let i = 0; i < lineIndex; i++) {
      height += this.getHeightOfLine(i);
    }
    return height;
  }

  /**
   * Get the font size for a specific line, considering character-specific styles
   * @param {number} lineIndex - The line index
   * @param {number} [charIndex] - Optional character index within the line
   * @returns {number} Font size
   */
  _getLineFontSize(lineIndex, charIndex = 0) {
    // Check if there are character-specific styles
    if (this.styles && this.styles[lineIndex]) {
      const lineStyles = this.styles[lineIndex];
      if (lineStyles[charIndex] && lineStyles[charIndex].fontSize) {
        return lineStyles[charIndex].fontSize;
      }
      // If no style for specific char, check if any char in the line has fontSize
      const fontSizes = Object.values(lineStyles)
        .map((style) => style.fontSize)
        .filter((size) => size !== undefined);
      if (fontSizes.length > 0) {
        // Return the most common font size in the line
        return fontSizes[0];
      }
    }
    // Fall back to global font size
    return this.fontSize || 20;
  }

  /**
   * Get the font declaration for a specific line, considering character-specific styles
   * @param {number} lineIndex - The line index
   * @param {number} [charIndex] - Optional character index within the line
   * @returns {string} Font declaration string
   */
  _getLineFontDeclaration(lineIndex, charIndex = 0) {
    const fontSize = this._getLineFontSize(lineIndex, charIndex);
    const fontStyle = this.fontStyle || 'normal';
    const fontWeight = this.fontWeight || 'normal';
    const fontFamily = this.fontFamily || 'Times New Roman';

    return `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
  }

  /**
   * Measure text width accounting for character-specific styles
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {string} text - Text to measure
   * @param {number} lineIndex - Line index
   * @param {number} startCharIndex - Starting character index in the line
   * @returns {number} Total width
   */
  _measureTextWithStyles(ctx, text, lineIndex, startCharIndex) {
    let width = 0;
    const hasLineStyles = this.styles && this.styles[lineIndex];

    if (!hasLineStyles) {
      // No character-specific styles, use line font
      ctx.font = this._getLineFontDeclaration(lineIndex);
      return ctx.measureText(text).width;
    }

    // Measure character by character when there are styles
    for (let i = 0; i < text.length; i++) {
      const charIndex = startCharIndex + i;
      const lineStyles = this.styles[lineIndex];

      // Check if this specific character has a different font size
      if (lineStyles[charIndex] && lineStyles[charIndex].fontSize) {
        ctx.font = this._getLineFontDeclaration(lineIndex, charIndex);
      } else {
        // Use line's default font
        ctx.font = this._getLineFontDeclaration(lineIndex);
      }

      width += ctx.measureText(text[i]).width;
    }

    return width;
  }

  /**
   * Draw a wavy line (for spell check underline) with proper scaling
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x1 - Start X
   * @param {number} y1 - Start Y
   * @param {number} x2 - End X
   * @param {number} y2 - End Y
   * @param {number} [fontSize] - Optional font size for scaling
   */
  // eslint-disable-next-line no-unused-vars
  _drawWavyLine(ctx, x1, y1, x2, y2, fontSize) {
    const { wavyAmplitude, wavyWavelength } = this._getScaledProperties(
      fontSize
    );
    const distance = Math.abs(x2 - x1);
    const baselineY = y1;

    // Create smooth wave with more steps for smoother curves
    const steps = Math.max(8, Math.ceil(distance / 2)); // More steps for smoother wave

    ctx.beginPath();
    ctx.moveTo(x1, baselineY);

    for (let i = 0; i <= steps; i++) {
      const progress = i / steps;
      const x = x1 + progress * distance;

      // Create smooth sine wave
      const wavePhase = ((progress * distance) / wavyWavelength) * Math.PI * 2;
      const y = baselineY + Math.sin(wavePhase) * wavyAmplitude;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }

    ctx.stroke();
  }

  /**
   * Override _render to add spell check underlines
   * @param {CanvasRenderingContext2D} ctx
   */
  _render(ctx) {
    // First render the text normally
    super._render(ctx);

    // Then add spell check underlines if not in editing mode
    if (
      this.spellCheckEnabled &&
      this.spellCheckResults &&
      this.spellCheckResults.matches &&
      this.spellCheckResults.matches.length > 0
    ) {
      this._spellCheckUnderlinesDrawn = true;
      this._renderSpellCheckUnderlines(ctx);
    }
  }

  /**
   * Get current text metrics (with proper cache invalidation)
   * @returns {object} Current text metrics
   */
  _getTextMetrics() {
    if (!this._cachedTextMetrics) {
      const fullText = this.text || '';
      this._cachedTextMetrics = {
        fullText,
        textLines: fullText.split('\n'),
        // Don't cache lineHeight as it becomes stale when font properties change
      };
    }
    return this._cachedTextMetrics;
  }

  /**
   * Get the actual rendered lines of text (accounting for word wrapping)
   * @returns {Array<string>} Array of rendered lines
   */
  _getRenderedLines() {
    // Use Fabric.js internal _textLines if available (accounts for wrapping)
    if (this._textLines && this._textLines.length > 0) {
      return this._textLines.map((line) => line.join(''));
    }

    // Fallback to simple line splitting
    return (this.text || '').split('\n');
  }

  /**
   * Find which rendered line contains a character at the given offset
   * @param {number} offset - Character offset in the full text
   * @returns {object|null} Line information
   */
  _findLineForOffset(offset) {
    const originalText = this.text || '';
    const renderedLines = this._getRenderedLines();

    // Rebuild the text as it would appear visually, tracking character positions
    let reconstructedText = '';
    const lineMapping = [];

    for (let lineIndex = 0; lineIndex < renderedLines.length; lineIndex++) {
      const line = renderedLines[lineIndex];
      const lineStart = reconstructedText.length;

      lineMapping.push({
        lineIndex,
        line,
        startOffset: lineStart,
        endOffset: lineStart + line.length,
      });

      reconstructedText += line;

      // Add space or newline between lines
      if (lineIndex < renderedLines.length - 1) {
        // Check if this is a word wrap (no space needed) or manual break (space/newline needed)
        const currentLineEnd = reconstructedText.length;
        const nextCharInOriginal = originalText[currentLineEnd];

        if (nextCharInOriginal === '\n') {
          reconstructedText += '\n';
        } else if (nextCharInOriginal === ' ') {
          reconstructedText += ' ';
        }
        // For word wrapping, no character is added
      }
    }

    // Try to find a direct match first
    if (reconstructedText === originalText) {
      // Perfect match - use direct mapping
      for (const mapping of lineMapping) {
        if (offset >= mapping.startOffset && offset < mapping.endOffset) {
          return {
            lineIndex: mapping.lineIndex,
            line: mapping.line,
            lineStart: mapping.startOffset,
            lineEnd: mapping.endOffset,
            charIndexInLine: offset - mapping.startOffset,
          };
        }
      }
    } else {
      // Fallback: try to find the word that contains our offset
      const targetWord = this._getWordAtOffset(originalText, offset);
      if (targetWord) {
        for (let lineIndex = 0; lineIndex < renderedLines.length; lineIndex++) {
          const line = renderedLines[lineIndex];
          const wordIndex = line.indexOf(targetWord.word);
          if (wordIndex !== -1) {
            const charIndexInLine = wordIndex + (offset - targetWord.start);
            return {
              lineIndex,
              line,
              lineStart: 0, // Approximate
              lineEnd: line.length,
              charIndexInLine: Math.max(0, charIndexInLine),
            };
          }
        }
      }
    }

    return null;
  }

  /**
   * Get the word that contains the given offset
   * @param {string} text - Full text
   * @param {number} offset - Character offset
   * @returns {object|null} Word info
   */
  _getWordAtOffset(text, offset) {
    // Find word boundaries around the offset
    let start = offset;
    let end = offset;

    // Find start of word
    while (start > 0 && /\w/.test(text[start - 1])) {
      start--;
    }

    // Find end of word
    while (end < text.length && /\w/.test(text[end])) {
      end++;
    }

    if (start < end) {
      return {
        word: text.substring(start, end),
        start,
        end,
      };
    }

    return null;
  }

  /**
   * Render spell check underlines in local coordinates with proper positioning
   * @param {CanvasRenderingContext2D} ctx
   */
  _renderSpellCheckUnderlines(ctx) {
    if (!this.spellCheckResults?.matches) {
      return;
    }

    const matches = this.spellCheckResults.matches;

    ctx.save();
    ctx.setLineDash([]);

    matches.forEach((match) => {
      if (
        !match ||
        typeof match.offset !== 'number' ||
        typeof match.length !== 'number'
      ) {
        return;
      }

      // Set color based on whether the match has replacements
      const hasReplacements =
        match.replacements && match.replacements.length > 0;
      ctx.strokeStyle = hasReplacements
        ? this.UNDERLINE_COLOR_FIXABLE
        : this.UNDERLINE_COLOR_INFORMATIONAL;

      this._renderSingleUnderline(ctx, match);
    });

    ctx.restore();
  }

  /**
   * Render a single underline for a spell check match
   * @param {CanvasRenderingContext2D} ctx
   * @param {object} match - Spell check match
   */
  _renderSingleUnderline(ctx, match) {
    const startLineInfo = this._findLineForOffset(match.offset);
    if (!startLineInfo) return;

    const endOffset = match.offset + match.length - 1;
    const endLineInfo = this._findLineForOffset(endOffset);
    if (!endLineInfo) return;

    // Handle single-line errors
    if (startLineInfo.lineIndex === endLineInfo.lineIndex) {
      this._renderSingleLineUnderline(
        ctx,
        startLineInfo,
        match.offset - startLineInfo.lineStart,
        match.length
      );
    } else {
      // Handle multi-line errors (rare but possible)
      this._renderMultiLineUnderline(ctx, startLineInfo, endLineInfo, match);
    }
  }

  /**
   * Render underline for a single line error
   * @param {CanvasRenderingContext2D} ctx
   * @param {object} lineInfo - Line information
   * @param {number} startChar - Start character in line
   * @param {number} length - Length of error
   */
  _renderSingleLineUnderline(ctx, lineInfo, startChar, length) {
    const { line, lineIndex } = lineInfo;

    // Get text before the error and the error text
    const beforeError = line.substring(0, startChar);
    const errorText = line.substring(startChar, startChar + length);

    // Skip if invalid bounds
    if (startChar < 0 || startChar >= line.length || errorText.length === 0) {
      return;
    }

    // Get line-specific font size and scaled properties
    const lineFontSize = this._getLineFontSize(lineIndex, startChar);
    const { underlineWidth, underlineOffset } = this._getScaledProperties(
      lineFontSize
    );

    // Set line width based on line-specific font size
    ctx.lineWidth = underlineWidth;

    // Measure text widths accounting for character-specific styles
    const beforeWidth = this._measureTextWithStyles(
      ctx,
      beforeError,
      lineIndex,
      0
    );
    const errorWidth = this._measureTextWithStyles(
      ctx,
      errorText,
      lineIndex,
      startChar
    );

    // Calculate position based on text alignment
    let startX = this._getLineStartX(ctx, line, lineIndex);
    startX += beforeWidth;

    // Calculate Y position using cumulative line heights
    const cumulativeHeight = this._getCumulativeLineHeight(lineIndex);
    const currentLineHeight = this.getHeightOfLine(lineIndex);
    const baselineY = -this.height / 2 + cumulativeHeight + currentLineHeight;
    const underlineY = baselineY + underlineOffset;

    // Draw the wavy underline
    this._drawWavyLine(
      ctx,
      startX,
      underlineY,
      startX + errorWidth,
      underlineY,
      lineFontSize
    );
  }

  /**
   * Render underline for multi-line errors
   * @param {CanvasRenderingContext2D} ctx
   * @param {object} startLineInfo - Start line information
   * @param {object} endLineInfo - End line information
   * @param {object} match - Spell check match
   */
  _renderMultiLineUnderline(ctx, startLineInfo, endLineInfo, match) {
    // For multi-line errors, underline each affected line
    for (
      let lineIndex = startLineInfo.lineIndex;
      lineIndex <= endLineInfo.lineIndex;
      lineIndex++
    ) {
      const renderedLines = this._getRenderedLines();
      const line = renderedLines[lineIndex];

      let startChar, endChar;

      if (lineIndex === startLineInfo.lineIndex) {
        // First line: from error start to end of line
        startChar = match.offset - startLineInfo.lineStart;
        endChar = line.length;
      } else if (lineIndex === endLineInfo.lineIndex) {
        // Last line: from start of line to error end
        startChar = 0;
        endChar = match.offset + match.length - endLineInfo.lineStart;
      } else {
        // Middle lines: entire line
        startChar = 0;
        endChar = line.length;
      }

      // Render underline for this portion
      const beforeError = line.substring(0, startChar);
      const errorText = line.substring(startChar, endChar);

      if (errorText.length > 0) {
        // Get line-specific font size and scaled properties
        const lineFontSize = this._getLineFontSize(lineIndex, startChar);
        const { underlineWidth, underlineOffset } = this._getScaledProperties(
          lineFontSize
        );

        // Set line width based on line-specific font size
        ctx.lineWidth = underlineWidth;

        // Measure text widths accounting for character-specific styles
        const beforeWidth = this._measureTextWithStyles(
          ctx,
          beforeError,
          lineIndex,
          0
        );
        const errorWidth = this._measureTextWithStyles(
          ctx,
          errorText,
          lineIndex,
          startChar
        );

        let startX = this._getLineStartX(ctx, line, lineIndex);
        startX += beforeWidth;

        // Calculate Y position using cumulative line heights
        const cumulativeHeight = this._getCumulativeLineHeight(lineIndex);
        const currentLineHeight = this.getHeightOfLine(lineIndex);
        const baselineY =
          -this.height / 2 + cumulativeHeight + currentLineHeight;
        const underlineY = baselineY + underlineOffset;

        this._drawWavyLine(
          ctx,
          startX,
          underlineY,
          startX + errorWidth,
          underlineY
        );
      }
    }
  }

  /**
   * Calculate the starting X position for a line based on text alignment
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} lineText
   * @param {number} lineIndex
   * @returns {number} Starting X position
   */
  _getLineStartX(ctx, lineText, lineIndex) {
    let x = -this.width / 2;

    if (this.textAlign === 'center') {
      const lineWidth = this._measureTextWithStyles(
        ctx,
        lineText,
        lineIndex,
        0
      );
      x = -lineWidth / 2;
    } else if (this.textAlign === 'right') {
      const lineWidth = this._measureTextWithStyles(
        ctx,
        lineText,
        lineIndex,
        0
      );
      x = this.width / 2 - lineWidth;
    }

    return x;
  }

  /**
   * Get all spell check errors
   * @returns {Array} Array of spell check errors
   */
  getSpellCheckErrors() {
    return this.spellCheckResults?.matches || [];
  }

  /**
   * Check if textbox has any spell check errors
   * @returns {boolean}
   */
  hasSpellCheckErrors() {
    return this.spellCheckResults?.matches?.length > 0;
  }

  /**
   * Check if textbox has errors with available replacements
   * @returns {boolean}
   */
  hasFixableErrors() {
    if (!this.spellCheckResults?.matches) {
      return false;
    }

    return this.spellCheckResults.matches.some(
      (match) => match.replacements && match.replacements.length > 0
    );
  }

  /**
   * Override __onMouseDown to handle control clicks during editing
   * @param {Event} e - Mouse event
   */
  __onMouseDown(e) {
    // When editing, check if we're clicking on the spell check control
    if (this.isEditing && this.canvas) {
      const pointer = this.canvas.getPointer(e);
      const corner = this._findTargetCorner(pointer);

      if (corner === 'fixSpelling' && this.controls.fixSpelling) {
        // Handle the spell check control click
        const control = this.controls.fixSpelling;
        if (control.mouseUpHandler) {
          control.mouseUpHandler(e, { target: this });
          // Don't call the parent method to prevent exiting edit mode
          return;
        }
      }
    }

    // Call parent method for normal behavior
    super.__onMouseDown && super.__onMouseDown(e);
  }

  /**
   * Override _findTargetCorner to ensure control detection during editing
   * @param {Object} pointer - Pointer coordinates
   * @param {boolean} forTouch - Whether this is for touch interaction
   * @returns {string|boolean} Corner name or false
   */
  _findTargetCorner(pointer, forTouch) {
    // If we're editing and have spell check errors, temporarily enable corner detection
    const originalEvented = this.evented;
    const originalHasControls = this.hasControls;

    if (this.isEditing && this.hasSpellCheckErrors()) {
      this.evented = true;
      this.hasControls = true;
    }

    // Call parent method
    const corner = super._findTargetCorner(pointer, forTouch);

    // Restore original values
    this.evented = originalEvented;
    this.hasControls = originalHasControls;

    return corner;
  }

  /**
   * Override _renderControls to ensure spell check control is visible during editing
   * @param {CanvasRenderingContext2D} ctx
   * @param {Object} styleOverride
   */
  _renderControls(ctx, styleOverride) {
    // If we're editing and have spell check errors, ensure controls are rendered
    const originalHasControls = this.hasControls;
    const originalEvented = this.evented;

    if (this.isEditing && this.hasSpellCheckErrors()) {
      this.hasControls = true;
      this.evented = true; // Allow control events during editing
    }

    // Call parent method
    super._renderControls && super._renderControls(ctx, styleOverride);

    // Restore original values
    this.hasControls = originalHasControls;
    this.evented = originalEvented;
  }

  /**
   * Override toObject to include spellcheck data for serialization
   * @param {Array} propertiesToInclude
   * @returns {Object}
   */
  toObject(propertiesToInclude) {
    const obj = super.toObject(propertiesToInclude);

    // Don't save spellcheck results to maintain backward compatibility
    // Results will be regenerated when needed

    return obj;
  }

  /**
   * Static method to create from existing fabric.Textbox
   * @param {fabric.Textbox} textbox
   * @returns {SpellCheckTextbox}
   */
  static fromTextbox(textbox) {
    // Get serialized object with all properties
    const options = textbox.toObject();

    // Create new SpellCheckTextbox with text and options
    const spellCheckTextbox = new SpellCheckTextbox(textbox.text, options);

    // Copy canvas reference if exists
    if (textbox.canvas) {
      spellCheckTextbox.canvas = textbox.canvas;
    }

    // Copy internal fabric properties that might not be in toObject()
    if (textbox.__charBounds) {
      spellCheckTextbox.__charBounds = textbox.__charBounds;
    }

    if (textbox._textLines) {
      spellCheckTextbox._textLines = textbox._textLines;
    }

    // Ensure type remains 'textbox' for backward compatibility
    spellCheckTextbox.type = 'textbox';

    return spellCheckTextbox;
  }
}

// Register the class with fabric
fabric.SpellCheckTextbox = SpellCheckTextbox;

/**
 * Creates an instance of SpellCheckTextbox from an object representation
 * @static
 * @memberOf SpellCheckTextbox
 * @param {Object} object Object to create an instance from
 * @param {Function} callback Callback to invoke when an instance is created
 */
SpellCheckTextbox.fromObject = function (object, callback) {
  // Create new SpellCheckTextbox instance
  const textbox = new SpellCheckTextbox(object.text, object);

  // Ensure the instance is properly initialized
  if (callback) {
    callback(textbox);
  }

  return textbox;
};

// Store the original fabric.Textbox for compatibility if needed
const OriginalTextbox = fabric.Textbox;
const OriginalFromObject = OriginalTextbox.fromObject;

// Override fabric.Textbox.fromObject to use SpellCheckTextbox
// This ensures that all deserialized textboxes become SpellCheckTextbox instances
OriginalTextbox.fromObject = function (object, callback) {
  // Check if we should use SpellCheckTextbox based on whether convertTextboxesToSpellCheck will run
  // This will be determined by the feature flag check in convertTextboxesToSpellCheck
  // For now, always create SpellCheckTextbox instances for type 'textbox'
  return SpellCheckTextbox.fromObject(object, callback);
};

// Store original fromObject for potential restoration
SpellCheckTextbox.OriginalFromObject = OriginalFromObject;
